import React, {useContext, useEffect, useRef, useState} from "react";
import DashboardFarmDisplay from "../../generated/interfaces/dashboardFarmDisplay";
import PagedSearchTable, {PagedTableFunctions} from "../../components/PagedSearchTable";
import FarmController from "../../generated/controllers/farmController";
import Dialog from "../../components/Dialog";
import LocationDataGrouped from "../../generated/interfaces/locationDataGrouped";
import BlockRowDisplay from "../../generated/interfaces/blockRowDisplay";
import {dateFormat} from "../../date";
import AppContext from "../appContext";
import Success from "../../components/Success";
import DeckGLMap from "./DeckGLMap";
import BlockEditDialog, {unitText} from "./BlockEditDialog";
import SeasonSelect from "./SeasonSelect";
import ReadyForProcessingTab from "./ReadyForProcessingTab";
import PermissionEnum from "../../generated/interfaces/permissionEnum";
import Failed from "../../components/Failed";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import {Geometry} from "ol/geom";
import {
    farmBoundaryStyle,
    landBoundaryStyle,
    makeVectorLayer,
    readGeoJsonFeature,
    readGeoJsonFeatureCollection
} from "../../helpers/map";
import {PaginationRequestSearchEx} from "../../generated/request";
import OpenLayerMapComponent from "../OpenLayerMapComponent";

const emptyDashboardFarmDisplay: DashboardFarmDisplay = {
    id: 0,
    mfwId: 0,
    client: '',
    name: '',
    boundaryUID: '',
    pickCounts: 0,
    lastUpdated: null,
    start: new Date(),
    end: new Date(),
    shapeFileGenerated: null
}

type ViewTab = 'blocks' | 'ready'

const Farms: React.FC = () => {
    const pagedTableRef = useRef<PagedTableFunctions<DashboardFarmDisplay>>()
    const landsPagedRef = useRef<PagedTableFunctions<BlockRowDisplay>>()

    const context = useContext(AppContext)
    // Gates block editing + the whole yield-processing flow. Without it a user still sees blocks
    // read-only but cannot edit weights/crops/cultivars or open the Ready-for-Processing tab.
    const canProcess = context.hasPermission(PermissionEnum.Dashboard_YieldProcessing)

    const [landsArgs, setLandsArgs] = useState<{id: number, seasonId: number | null}>({id: 0, seasonId: null})
    const [landsDialog, setLandsDialog] = useState(false)
    const [tab, setTab] = useState<ViewTab>('blocks')
    const [selectedFarm, setSelectedFarm] = useState<DashboardFarmDisplay>(emptyDashboardFarmDisplay)
    const [mapDialog, setMapDialog] = useState(false)
    const [editBlock, setEditBlock] = useState<BlockRowDisplay | null>(null)
    const [editBlockShow, setEditBlockShow] = useState(false)
    const [points, setPoints] = useState<LocationDataGrouped[]>([])
    const [displayData, setDisplayData] = useState<any>({})
    const [farmMapDialog, setFarmMapDialog] = useState(false)
    const [mapLayers, setMapLayers] = useState<VectorLayer<VectorSource<Geometry>>[]>([])

    function select(row: DashboardFarmDisplay) {
        setSelectedFarm(row)
        setLandsArgs({id: row.id, seasonId: null})
        setTab('blocks')
        setLandsDialog(true)
    }

    function viewMap(row: DashboardFarmDisplay) {
        setSelectedFarm(row)
        const hideLoader = context.showLoader()

        FarmController.geoJson(row.id).then((response) => {
            if (response.farm == "" && response.lands.length == 0) {
                context.showSnack(<Failed title='Failed' text='No farm boundary data exists. Please refresh from MFW.'/>)
                hideLoader()
                return
            }
            let farmBoundary = makeVectorLayer(readGeoJsonFeatureCollection(response.farm), farmBoundaryStyle)
            let landBoundaries = makeVectorLayer(response.lands.filter(l => l.length > 0).map(readGeoJsonFeature), landBoundaryStyle)

            setMapLayers([farmBoundary, landBoundaries])
            setFarmMapDialog(true)
            hideLoader()
        }).catch(() => hideLoader())
    }

    function getPoints(row: BlockRowDisplay) {
        const hideLoader = context.showLoader()
        FarmController.locationData({landId: row.landId, seasonId: landsArgs.seasonId}).then(resp => {
            setPoints(resp)
            setMapDialog(true)
            setDisplayData({farmName: selectedFarm.name, blockName: row.name})
        }).finally(() => hideLoader())
    }

    function downloadLocationData(row: BlockRowDisplay) {
        FarmController.downloadLocationData({landId: row.landId, seasonId: landsArgs.seasonId})
        context.showSnack(<Success title={"Starting download"}/>)
    }

    function downloadLocationDataDay(row: BlockRowDisplay) {
        FarmController.downloadLocationDataGroupedDay({landId: row.landId, seasonId: landsArgs.seasonId})
        context.showSnack(<Success title={"Starting download"}/>)
    }

    function editRow(row: BlockRowDisplay) {
        // The re-approval warning fires at save time (in BlockEditDialog), not on open.
        setEditBlock(row)
        setEditBlockShow(true)
    }

    function searchBlocks(request: PaginationRequestSearchEx<BlockRowDisplay>) {
        return FarmController.blocksForEdit({...request, ...landsArgs})
    }

    useEffect(() => {
        landsPagedRef.current?.refresh()
    }, [landsArgs.seasonId])

    function renderActions(item: DashboardFarmDisplay) {
        return <>
            <div className="btn-sm btn-primary" onClick={() => select(item)}>view</div>
        </>
    }

    const tabButton = (id: ViewTab, label: string) => (
        <button
            className={`px-3 py-1 text-sm rounded-t ${tab === id ? 'bg-primary text-white' : 'text-gray-600'}`}
            onClick={() => setTab(id)}>
            {label}
        </button>
    )

    return (
        <div>
            <PagedSearchTable
                componentRef={pagedTableRef}
                call={FarmController.available}
                columns={[{
                    header: 'Client', key: 'clientName', row: (item) => item.client
                }, {
                    header: 'Name', key: 'name', row: (item) => item.name
                }, {
                    header: 'Season', key: 'season', row: (item) => `${dateFormat(item.start)} to ${dateFormat(item.end)}`
                }, {
                    header: 'Total Picks', key: 'totalPicks', row: (item) => item.pickCounts
                }, {
                    header: 'Last Updated', key: 'lastUpdated', row: (item) => dateFormat(item.lastUpdated, '%d %M %Y %H:%i:%s')
                }, {
                    header: 'Actions', key: 'actions', row: renderActions
                }]}/>

            <Dialog title={`View: ${selectedFarm.name ?? ''}`}
                    show={landsDialog}
                    setShow={setLandsDialog}
                    body={
                        <div>
                            <div className="flex items-center justify-between m-2">
                                <SeasonSelect farmId={selectedFarm.id} value={landsArgs.seasonId} onChange={seasonId => setLandsArgs(prev => ({...prev, seasonId}))}/>
                                <button className="btn btn-sm btn-primary" onClick={() => viewMap(selectedFarm)}>View farm</button>
                            </div>

                            <div className="flex gap-1 mx-2 border-b">
                                {tabButton('blocks', 'Blocks')}
                                {canProcess && tabButton('ready', 'Ready for Processing')}
                            </div>

                            {tab === 'ready' && canProcess
                                ? <ReadyForProcessingTab farmId={selectedFarm.id} seasonId={landsArgs.seasonId}/>
                                : (
                                    <div className="mx-2 mt-3">
                                        <PagedSearchTable
                                            call={w => searchBlocks({...w, sortBy: 'lastUpdated', ascending: false})}
                                            componentRef={landsPagedRef}
                                            keyExtractor={row => row.landId}
                                            columns={[
                                                {header: 'Name', key: 'name', row: item => item.name},
                                                {header: 'Total Picks', key: 'totalPicks', row: item => item.pickCounts},
                                                {header: 'Last Updated', key: 'lastUpdated', row: item => dateFormat(item.lastUpdated, '%d %M %Y')},
                                                {header: 'Weight', key: 'weight', row: item => item.value != null ? item.value : '-'},
                                                {header: 'Unit', key: 'unit', row: item => item.unit != null ? unitText[item.unit] : '-'},
                                                {header: 'Crop', key: 'crop', row: item => item.cropName ?? '-'},
                                                {header: 'Cultivar', key: 'cultivar', row: item => item.cultivarName ?? '-'},
                                                {
                                                    header: <div className="text-right">Actions</div>,
                                                    key: 'actions',
                                                    row: item => <div className='flex items-center justify-end'>
                                                        {canProcess && <div className="btn-sm mr-1 btn-primary" onClick={() => editRow(item)}>Edit</div>}
                                                        <div className="btn-sm mr-1 btn-primary" onClick={() => getPoints(item)}>View</div>
                                                        <div className="inline-block mr-1 cursor-pointer" onClick={() => downloadLocationData(item)}><img className='w-[25px] h-[25px]' src='/images/csv.png' alt=""/></div>
                                                        <div className="inline-block cursor-pointer" onClick={() => downloadLocationDataDay(item)}><img className='w-[25px] h-[25px]' src='/images/csv.png' alt=""/></div>
                                                    </div>
                                                }
                                            ]}
                                        />
                                    </div>
                                )}
                        </div>
                    }
                    footer={
                        <button className="btn btn-sm bg-red-500" onClick={() => setLandsDialog(false)}>Close</button>
                    }
            />

            <Dialog title={'View Farm'}
                    body={
                        <div className="relative flex w-screen h-screen">
                            <div className="w-full p-4 text-center">
                                <span className="text-3xl">Farm: {selectedFarm.name}</span>
                                <OpenLayerMapComponent layers={mapLayers}
                                                       tiffUrl={`/api/protected_tiffs/${selectedFarm.id}.4E-05.tiff`}/>
                            </div>
                        </div>
                    }
                    show={farmMapDialog}
                    setShow={setFarmMapDialog}
                    key="farmMapDialog"/>

            {
                mapDialog && points.length > 0
                    ? <DeckGLMap setShow={setMapDialog} points={points} displayData={displayData} landsArgsSeasonId={landsArgs.seasonId}/>
                    : null
            }

            {
                editBlock
                    ? <BlockEditDialog show={editBlockShow} setShow={setEditBlockShow} block={editBlock} seasonId={landsArgs.seasonId} onSaved={() => landsPagedRef.current?.refresh()}/>
                    : null
            }
        </div>
    )
}

export default Farms;
